// diffOpShim — map a DOMAIN structural diff op onto the UI `InlineDiff` op.
//
// The version-diff lens folds two reconstructed material states and asks the
// pure `@bilda/core` `Resource.DiffTrees.diffTrees` for the NET change — a
// list of `ElementDiff { path, op }` where `op` is the domain `DiffOp` sum
// (`add | remove | scalar-edit | move`). `@bilda/client`'s `InlineDiff` renders a
// PARALLEL op sum (`Editor/types.ts` `DiffOp`), widened with `list-edit` /
// `inline-edit` that a state diff over the opaque domain tree cannot derive.
// The two sums are parallel by design — `@bilda/client` imports zero `@bilda/core`
// (and vice-versa), so they are never the same type. This shim is the one
// pure boundary that carries a domain op across to its UI mirror.
//
// Because `@bilda/client` cannot import the domain type, `DomainDiffOp` below is a
// STRUCTURAL MIRROR of `@bilda/core`'s `Resource.DiffTrees.DiffOp` (same
// discipline as `Editor/types.ts` mirroring the domain element shapes). The
// integrator hands this shim the real core values at the shell boundary; they
// match structurally, so no cast is needed on either side.
//
// Two presentation seams the domain op leaves to the shell (no-dev-jargon):
//   • scalar-edit `field` is a MACHINE token (`"text"`, `"type"`, an attr key)
//     — localised to an author-facing label here via `labels.field`.
//   • move `from` / `to` are sibling INDICES — localised to position phrases
//     via `labels.position`.
// Both are injected as pure functions so localisation stays a caller concern
// and this shim holds no i18n of its own. Pure + total: a `match` over the
// discriminant, one arm per variant, exhaustive.

export type {
    DiffOpLabels,
    DomainAddOp,
    DomainDiffOp,
    DomainMoveOp,
    DomainRemoveOp,
    DomainScalarEditOp,
};
export { toUiDiffOp };

import type { DiffOp } from "../Editor/types.js";

// === DomainDiffOp — structural mirror of core `Resource.DiffTrees.DiffOp` ===

// A new element appeared in `b` that was absent from `a`.
type DomainAddOp = { readonly type: "add" };
// An element present in `a` is gone from `b`.
type DomainRemoveOp = { readonly type: "remove" };
// A single-valued field changed. `field` is a machine token; `before`/`after`
// are the stringified old/new values (an added field → empty `before`; a
// removed field → empty `after`).
type DomainScalarEditOp = {
    readonly type: "scalar-edit";
    readonly field: string;
    readonly before: string;
    readonly after: string;
};
// A same-id element changed position. `from`/`to` are sibling indices.
type DomainMoveOp = {
    readonly type: "move";
    readonly from: number;
    readonly to: number;
};

type DomainDiffOp =
    | DomainAddOp
    | DomainRemoveOp
    | DomainScalarEditOp
    | DomainMoveOp;

// The presentation seams the domain op leaves open — injected, not owned here.
type DiffOpLabels = {
    // Machine field token → author-facing field label ("text" → "Question text").
    readonly field: (fieldToken: string) => string;
    // Sibling index → author-facing position phrase (0 → "position 1").
    readonly position: (index: number) => string;
};

// Map one domain op onto its UI mirror. Pure + total.
const toUiDiffOp = (op: DomainDiffOp, labels: DiffOpLabels): DiffOp => {
    switch (op.type) {
        case "add":
            return { type: "add" };
        case "remove":
            return { type: "remove" };
        case "scalar-edit":
            // A state diff over the flat-string domain tree can only ever be a
            // scalar edit (never `inline-edit`, which needs a typed RichText
            // tree the domain Element does not carry) — so the mirror lands on
            // `scalar-edit`, whose legal review outcomes match.
            return {
                type: "scalar-edit",
                field: labels.field(op.field),
                before: op.before,
                after: op.after,
            };
        case "move":
            return {
                type: "move",
                from: labels.position(op.from),
                to: labels.position(op.to),
            };
        default: {
            // Exhaustiveness: a new DomainDiffOp variant fails to compile here.
            const exhaustive: never = op;
            return exhaustive;
        }
    }
};
